/**
 * Tickets-local helper for deriving saved-view column sets from per-field
 * `savedViewMembership` declarations.
 *
 * Each ticket field declares which saved views it appears in. This helper
 * inverts that mapping so saved-view definitions become a projection of the
 * field declarations rather than the source of truth — keeping field
 * declarations self-contained.
 *
 * Iteration order over `fields` (object property order) determines the
 * order of the returned ids. Field bundles are spread into the combined map
 * in a fixed order in `../fields/index.ts`, so the result is stable.
 *
 * Generic `TId` lets callers keep a literal field-id union (`TicketFieldId`)
 * so the result is compile-time compatible with
 * `SavedViewConfig<TicketFieldId>["visibleColumns"]`.
 */
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";

export function collectFieldsForSavedView<TId extends string>(
  viewId: string,
  fields: Readonly<Record<TId, SimpleFieldConfig>>
): TId[] {
  const ids: TId[] = [];
  for (const key of Object.keys(fields) as TId[]) {
    const membership = fields[key].savedViewMembership;
    if (membership && membership.includes(viewId)) {
      ids.push(key);
    }
  }
  return ids;
}
